import React, { useState } from 'react';
import { MessageSquare, Send, CheckCircle2, AlertCircle, Lightbulb, Bug, MessageCircle, HelpCircle, Menu } from 'lucide-react';
import { MainSidebar } from '../components/layout/MainSidebar';

type FeedbackCategory = 'feature_request' | 'bug_report' | 'general' | 'question';

interface FeedbackPageProps {
  userEmail?: string | null;
  isAnonymous?: boolean;
  onSignIn?: () => void;
  onSignOut?: () => void;
}

export const FeedbackPage: React.FC<FeedbackPageProps> = ({
  userEmail = null,
  isAnonymous = false,
  onSignIn = () => {},
  onSignOut = () => {},
}) => {
  const [category, setCategory] = useState<FeedbackCategory>('feature_request');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMainSidebarOpen, setIsMainSidebarOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!message.trim()) {
      setError('Please enter your feedback message.');
      return;
    }

    if (message.trim().length < 10) {
      setError('Please provide more details (at least 10 characters).');
      return;
    }

    setIsSubmitting(true);

    // Simulate submission (replace with actual API call later)
    setTimeout(() => {
      console.log('Feedback submitted:', {
        category,
        message,
        email: email || 'Not provided',
        timestamp: new Date().toISOString(),
        url: window.location.href,
      });

      setSubmitted(true);
      setIsSubmitting(false);

      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setMessage('');
        setEmail('');
        setCategory('feature_request');
      }, 3000);
    }, 1000);
  };

  const getCategoryIcon = (cat: FeedbackCategory) => {
    switch (cat) {
      case 'feature_request':
        return <Lightbulb className="w-5 h-5" />;
      case 'bug_report':
        return <Bug className="w-5 h-5" />;
      case 'general':
        return <MessageCircle className="w-5 h-5" />;
      case 'question':
        return <HelpCircle className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (cat: FeedbackCategory) => {
    switch (cat) {
      case 'feature_request':
        return 'border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100';
      case 'bug_report':
        return 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100';
      case 'general':
        return 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100';
      case 'question':
        return 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100';
    }
  };

  const getActiveCategoryColor = (cat: FeedbackCategory) => {
    switch (cat) {
      case 'feature_request':
        return 'border-purple-500 bg-purple-100 text-purple-900 ring-2 ring-purple-300';
      case 'bug_report':
        return 'border-red-500 bg-red-100 text-red-900 ring-2 ring-red-300';
      case 'general':
        return 'border-blue-500 bg-blue-100 text-blue-900 ring-2 ring-blue-300';
      case 'question':
        return 'border-green-500 bg-green-100 text-green-900 ring-2 ring-green-300';
    }
  };

  return (
    <div className="flex flex-col min-h-screen w-full bg-gray-50">
      {/* Main App Sidebar - Overlay */}
      <MainSidebar
        isOpen={isMainSidebarOpen}
        onClose={() => setIsMainSidebarOpen(false)}
        alwaysOverlay={true}
        userEmail={userEmail}
        isAnonymous={isAnonymous}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
      />
      
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-200 w-full">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsMainSidebarOpen(!isMainSidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100"
              title="Toggle main menu"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
            <MessageSquare className="w-6 h-6 text-green-600" />
            <h1 className="text-xl font-bold text-gray-900">Share Your Feedback</h1>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="max-w-4xl mx-auto py-12">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Help Us Improve CommsGenie
          </h2>
          <p className="text-lg text-gray-700">
            Your feedback shapes the future of CommsGenie. Whether it's a feature request, 
            bug report, or just a thought, we want to hear from you.
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8">
          {submitted ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Thank You!
              </h3>
              <p className="text-gray-700 mb-4">
                Your feedback has been received. We review all submissions and will follow up 
                if needed.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="text-green-600 hover:text-green-700 font-medium"
              >
                Submit More Feedback
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category selection */}
              <div>
                <div className="block text-sm font-semibold text-gray-900 mb-3">
                  What type of feedback are you sharing?
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCategory('feature_request')}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg border-2 transition-all ${
                      category === 'feature_request'
                        ? getActiveCategoryColor('feature_request')
                        : getCategoryColor('feature_request')
                    }`}
                  >
                    {getCategoryIcon('feature_request')}
                    <span className="font-medium">Feature Request</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setCategory('bug_report')}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg border-2 transition-all ${
                      category === 'bug_report'
                        ? getActiveCategoryColor('bug_report')
                        : getCategoryColor('bug_report')
                    }`}
                  >
                    {getCategoryIcon('bug_report')}
                    <span className="font-medium">Bug Report</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setCategory('general')}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg border-2 transition-all ${
                      category === 'general'
                        ? getActiveCategoryColor('general')
                        : getCategoryColor('general')
                    }`}
                  >
                    {getCategoryIcon('general')}
                    <span className="font-medium">General Feedback</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setCategory('question')}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg border-2 transition-all ${
                      category === 'question'
                        ? getActiveCategoryColor('question')
                        : getCategoryColor('question')
                    }`}
                  >
                    {getCategoryIcon('question')}
                    <span className="font-medium">Question</span>
                  </button>
                </div>
              </div>

              {/* Message textarea */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                  Your Feedback *
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    category === 'feature_request'
                      ? 'What feature would make CommsGenie more useful for your practice?'
                      : category === 'bug_report'
                      ? 'Please describe the issue you encountered and steps to reproduce it.'
                      : category === 'question'
                      ? 'What would you like to know about CommsGenie?'
                      : 'Share your thoughts with us...'
                  }
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {message.length} characters (minimum 10)
                </p>
              </div>

              {/* Optional email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Your Email (optional)
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Provide your email if you'd like us to follow up with you
                </p>
              </div>

              {/* Error message */}
              {error && (
                <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Submit Feedback</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Additional info */}
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Need Immediate Help?
            </h3>
            <p className="text-gray-700 mb-4">
              For urgent issues or questions, visit our support page for faster assistance.
            </p>
            <a
              href="/support"
              className="inline-flex items-center text-blue-700 hover:text-blue-800 font-medium"
            >
              Go to Support →
            </a>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-purple-900 mb-2">
              Check the Documentation
            </h3>
            <p className="text-gray-700 mb-4">
              Find guides and tutorials on how to use CommsGenie's features effectively.
            </p>
            <a
              href="/docs"
              className="inline-flex items-center text-purple-700 hover:text-purple-800 font-medium"
            >
              View Docs →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

